import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const { FiX, FiPrinter, FiCheck, FiClock, FiAlertTriangle, FiShoppingBag, FiTruck, FiPlusCircle } = FiIcons;

/**
 * Drawer / Modal Split-View Dettaglio Comande del Tavolo
 * Fornisce accesso istantaneo al conto, ai piatti ordinati e alle azioni operative.
 */
const TableOrderDrawer = ({
  isOpen = false,
  onClose,
  tableNumber,
  comande = [],
  onPrintReceipt,
  onCloseTable,
  onAddMoreDishes,
  onMarkDelivered,
  isProcessing = false,
  mode = 'cassa' // 'cassa' | 'cameriere'
}) => {
  if (!isOpen || !tableNumber) return null;

  // Filtra e normalizza le comande per questo tavolo
  const tableComande = comande.filter(c => c.tavolo === tableNumber && c.status !== 'pagata');

  // Calcola totale e verifica piatti in cucina
  let totalAmount = 0;
  let hasPendingInKitchen = false;

  tableComande.forEach(c => {
    const dishes = Array.isArray(c.piatti) ? c.piatti : [];
    dishes.forEach(p => {
      const qty = Number(p.quantita || p.quantity || 1);
      const price = Number(p.prezzo || 0);
      totalAmount += (price * qty);
    });

    if (!c.consegnata && c.status !== 'completata') {
      hasPendingInKitchen = true;
    }
  });

  const formatTime = (ts) => {
    try {
      return format(new Date(ts || Date.now()), 'HH:mm', { locale: it });
    } catch {
      return '--:--';
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
        {/* Backdrop semitrasparente */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        />

        {/* Drawer Pannello Laterale */}
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 250 }}
          className="relative w-full max-w-lg bg-white shadow-2xl z-10 flex flex-col h-full border-l border-gray-200"
        >
          {/* Header Drawer */}
          <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-black text-gray-900">Tavolo {tableNumber}</span>
                {hasPendingInKitchen ? (
                  <span className="px-2.5 py-0.5 text-xs font-semibold bg-orange-100 text-orange-800 rounded-full flex items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1 animate-pulse"></span>
                    Portate in cucina
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded-full flex items-center">
                    <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 mr-1" />
                    Tutto servito
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {tableComande.length} comande attive &bull; Totale: €{totalAmount.toFixed(2)}
              </p>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/50 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          {/* Avviso Portate Pendenti per la Cassa */}
          {hasPendingInKitchen && mode === 'cassa' && (
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-start space-x-2.5">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <span className="font-bold">Attenzione:</span> Questo tavolo ha piatti ancora in preparazione in cucina. Il conto totale include tutte le portate ordinate.
              </div>
            </div>
          )}

          {/* Lista Comande Dettagliata */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {tableComande.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <SafeIcon icon={FiShoppingBag} className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Nessuna comanda registrata per questo tavolo.</p>
              </div>
            ) : (
              tableComande.map((comanda, idx) => {
                const dishes = Array.isArray(comanda.piatti) ? comanda.piatti : [];
                const isDelivered = Boolean(comanda.consegnata);

                return (
                  <div key={comanda.id || idx} className="bg-gray-50 rounded-xl p-4 border border-gray-200/70">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-sm text-gray-900">
                          Comanda #{comanda.codice_comanda || comanda.id?.slice(0, 4)}
                        </span>
                        <span className="text-xs text-gray-400 flex items-center">
                          <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                          {formatTime(comanda.timestamp || comanda.created)}
                        </span>
                      </div>

                      {/* Pill Stato Comanda */}
                      {isDelivered ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          Servito
                        </span>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                            In cucina
                          </span>
                          {onMarkDelivered && (
                            <button
                              onClick={() => onMarkDelivered(comanda.id)}
                              className="text-xs text-[#5C715E] hover:underline font-medium"
                            >
                              Segna Servito
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Elenco Piatti */}
                    <div className="space-y-2">
                      {dishes.map((piatto, pIdx) => {
                        const qty = Number(piatto.quantita || piatto.quantity || 1);
                        const price = Number(piatto.prezzo || 0);

                        return (
                          <div key={pIdx} className="flex justify-between items-center text-sm">
                            <span className="text-gray-800">
                              <span className="font-bold text-[#5C715E] mr-2">{qty}x</span>
                              {piatto.nome}
                            </span>
                            <span className="font-semibold text-gray-900">
                              €{(price * qty).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Drawer con Totale & Azioni */}
          <div className="p-6 bg-white border-t border-gray-200 space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="text-sm font-medium text-gray-500">Totale Complessivo Conto:</span>
              <span className="text-3xl font-black text-emerald-700">€{totalAmount.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {mode === 'cassa' && (
                <>
                  {onPrintReceipt && (
                    <button
                      onClick={onPrintReceipt}
                      disabled={isProcessing || tableComande.length === 0}
                      className="flex items-center justify-center py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-sm transition-colors disabled:opacity-50"
                    >
                      <SafeIcon icon={FiPrinter} className="w-4 h-4 mr-2" />
                      Stampa Preconto
                    </button>
                  )}

                  {onCloseTable && (
                    <button
                      onClick={onCloseTable}
                      disabled={isProcessing || tableComande.length === 0}
                      className="flex items-center justify-center py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition-colors shadow-lg disabled:opacity-50"
                    >
                      <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                      Chiudi Tavolo (Pagato)
                    </button>
                  )}
                </>
              )}

              {mode === 'cameriere' && (
                <>
                  {onAddMoreDishes && (
                    <button
                      onClick={onAddMoreDishes}
                      className="col-span-2 flex items-center justify-center py-3.5 px-4 bg-[#5C715E] hover:bg-[#4B5C4D] text-white rounded-xl font-bold text-sm transition-all shadow-md"
                    >
                      <SafeIcon icon={FiPlusCircle} className="w-5 h-5 mr-2" />
                      Aggiungi Altri Piatti a questo Tavolo
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TableOrderDrawer;
